import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { useToast } from '../../utils/ToastContext'
import { trpc, trpcClient } from '../../utils/trpc'

/**
 * @fileoverview eNPS Detail Store
 *
 * Zustand store for managing eNPS configuration state and mutations
 */

interface EnpsFormData {
	name: string
	description: string | null
	cadence: 'one-time' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'annually'
	populationId: string | null
	emailTemplateId: string | null
	questionText: string
	followUpQuestionText: string
	textFeedbackEnabled: boolean
	allowExternalParticipants: boolean
	nextScheduledDate: Date | null
}

export interface EnpsData {
	id: string
	name: string
	description: string | null
	status: 'draft' | 'active' | 'paused' | 'archived'
	organizationId: string
	cadence: 'one-time' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'annually'
	populationId: string | null
	emailTemplateId: string | null
	questionText: string
	followUpQuestionText: string
	textFeedbackEnabled: boolean
	allowExternalParticipants: boolean
	nextScheduledDate: Date | null
	lastExecutionDate: Date | null
	createdAt: Date
	updatedAt: Date
}

interface Organization {
	id: string
	name: string
	logo?: string | null
}

interface EmailTemplate {
	id: string
	name: string
	subject: string | null
	body: string | null
}

// Available email variables for eNPS
const ENPS_VARIABLES = [
	{
		key: '{{enps.surveyUrl}}',
		example: 'https://app.example.com/enps/response/abc123?participantId=user456',
	},
	{
		key: '{{enps.surveyLink}}',
		example: '<a href="#" target="_blank" rel="noopener noreferrer">Click here to submit your NPS response</a>',
	},
	{
		key: '{{enps.questionText}}',
		example: 'How likely are you to recommend our organization as a place to work?',
	},
	{ key: '{{enps.name}}', example: 'Q4 2024 Employee Satisfaction Survey' },
	{ key: '{{users.firstName}}', example: 'John' },
	{ key: '{{users.lastName}}', example: 'Doe' },
	{ key: '{{users.fullName}}', example: 'John Doe' },
	{ key: '{{organizations.name}}', example: 'Acme Corp' },
]

interface EnpsState {
	enpsConfig: EnpsData | null
	organization: Organization | null
	isLoading: boolean
	isLoadingOrganization: boolean
	error: string | null

	// Email template state
	selectedTemplate: EmailTemplate | null
	isLoadingTemplate: boolean
	previewSubject: string
	previewBody: string

	setEnpsConfig: (config: EnpsData) => void
	resetEnpsConfig: () => void
	saveEnpsConfig: () => EnpsFormData
	updateEnpsConfig: (data: Partial<EnpsFormData>) => void
	setError: (error: string | null) => void

	// Data fetching
	fetchOrganization: (organizationId: string) => Promise<void>
	fetchEnpsConfig: (enpsId: string, organizationId: string) => Promise<void>
	fetchSelectedTemplate: (templateId: string, organizationId: string) => Promise<void>
	clearSelectedTemplate: () => void
	initializeNewConfig: (organizationId: string) => void

	useCreateEnpsConfig: () => {
		createEnpsConfig: (data: EnpsFormData) => Promise<void>
		isLoading: boolean
	}
	useEditEnpsConfig: (enpsId?: string) => {
		editEnpsConfig: (data: Partial<EnpsFormData>) => Promise<void>
		isLoading: boolean
	}
	useActivateEnpsConfig: (enpsId: string) => {
		activateEnpsConfig: () => Promise<void>
		isLoading: boolean
	}
	usePauseEnpsConfig: (enpsId: string) => {
		pauseEnpsConfig: () => Promise<void>
		isLoading: boolean
	}
	useArchiveEnpsConfig: (enpsId: string) => {
		archiveEnpsConfig: () => Promise<void>
		isLoading: boolean
	}
}

export const useEnpsStore = create<EnpsState>()(
	devtools(
		immer((set, get) => ({
			enpsConfig: null,
			organization: null,
			isLoading: false,
			isLoadingOrganization: false,
			error: null,

			// Email template state
			selectedTemplate: null,
			isLoadingTemplate: false,
			previewSubject: '',
			previewBody: '',

			// Data fetching actions
			fetchOrganization: async (organizationId: string) => {
				if (!organizationId) return

				set((state) => {
					state.isLoadingOrganization = true
					state.error = null
				})

				try {
					const result = await trpcClient.admin.organizations.getById.query({ id: organizationId })
					set((state) => {
						state.organization = { id: result.id, name: result.name }
						state.isLoadingOrganization = false
					})
				} catch (error) {
					set((state) => {
						state.error = error instanceof Error ? error.message : 'Failed to fetch organization'
						state.isLoadingOrganization = false
					})
				}
			},

			fetchEnpsConfig: async (enpsId: string, organizationId: string) => {
				if (!enpsId || enpsId === 'create' || !organizationId) return

				set((state) => {
					state.isLoading = true
					state.error = null
				})

				try {
					const result = await trpcClient.admin.enps.getById.query({ id: enpsId, organizationId })
					set((state) => {
						state.enpsConfig = {
							id: result.id,
							name: result.name,
							description: result.description,
							status: result.status,
							organizationId: result.organizationId,
							cadence: result.cadence,
							populationId: result.populationId,
							emailTemplateId: result.emailTemplateId,
							questionText: result.questionText,
							followUpQuestionText: result.followUpQuestionText,
							textFeedbackEnabled: result.textFeedbackEnabled,
							allowExternalParticipants: result.allowExternalParticipants,
							nextScheduledDate: result.nextScheduledDate ? new Date(result.nextScheduledDate) : null,
							lastExecutionDate: result.lastExecutionDate ? new Date(result.lastExecutionDate) : null,
							createdAt: new Date(result.createdAt),
							updatedAt: new Date(result.updatedAt),
						}
						state.isLoading = false
					})
				} catch (error) {
					set((state) => {
						state.error = error instanceof Error ? error.message : 'Failed to fetch NPS configuration'
						state.isLoading = false
					})
				}
			},

			fetchSelectedTemplate: async (templateId: string, organizationId: string) => {
				if (!templateId || !organizationId) {
					set((state) => {
						state.selectedTemplate = null
						state.previewSubject = ''
						state.previewBody = ''
					})
					return
				}

				set((state) => {
					state.isLoadingTemplate = true
				})

				try {
					const result = await trpcClient.admin.emailTemplates.getById.query({
						id: templateId,
						organizationId,
					})

					// Compute preview by replacing variables with example values
					let previewSubject = result.subject || ''
					let previewBody = result.body || ''

					ENPS_VARIABLES.forEach((variable) => {
						const regex = new RegExp(variable.key.replace(/[{}]/g, '\\$&'), 'gi')
						previewSubject = previewSubject.replace(regex, variable.example)
						previewBody = previewBody.replace(regex, variable.example)
					})

					set((state) => {
						state.selectedTemplate = {
							id: result.id,
							name: result.name,
							subject: result.subject,
							body: result.body,
						}
						state.previewSubject = previewSubject
						state.previewBody = previewBody
						state.isLoadingTemplate = false
					})
				} catch (error) {
					console.error('Failed to fetch email template:', error)
					set((state) => {
						state.selectedTemplate = null
						state.previewSubject = ''
						state.previewBody = ''
						state.isLoadingTemplate = false
					})
				}
			},

			clearSelectedTemplate: () => {
				set((state) => {
					state.selectedTemplate = null
					state.previewSubject = ''
					state.previewBody = ''
				})
			},

			initializeNewConfig: (organizationId: string) => {
				set((state) => {
					state.enpsConfig = {
						id: '',
						name: '',
						description: null,
						status: 'draft',
						organizationId,
						cadence: 'quarterly',
						populationId: null,
						emailTemplateId: null,
						questionText: 'How likely are you to recommend our organization as a place to work?',
						followUpQuestionText: 'What is the primary reason for your score?',
						textFeedbackEnabled: true,
						allowExternalParticipants: false,
						nextScheduledDate: null,
						lastExecutionDate: null,
						createdAt: new Date(),
						updatedAt: new Date(),
					}
				})
			},

			setEnpsConfig: (config: EnpsData) =>
				set((state) => {
					state.enpsConfig = config
				}),

			resetEnpsConfig: () =>
				set((state) => {
					state.enpsConfig = null
					state.organization = null
					state.error = null
					state.selectedTemplate = null
					state.previewSubject = ''
					state.previewBody = ''
				}),

			updateEnpsConfig: (data: Partial<EnpsFormData>) =>
				set((state) => {
					if (state.enpsConfig) {
						state.enpsConfig = {
							...state.enpsConfig,
							...data,
						}
					} else {
						state.enpsConfig = {
							id: '',
							name: '',
							description: null,
							status: 'draft',
							organizationId: '',
							cadence: 'quarterly',
							populationId: null,
							emailTemplateId: null,
							questionText: 'How likely are you to recommend our organization as a place to work?',
							followUpQuestionText: 'What is the primary reason for your score?',
							textFeedbackEnabled: true,
							allowExternalParticipants: false,
							nextScheduledDate: null,
							lastExecutionDate: null,
							createdAt: new Date(),
							updatedAt: new Date(),
							...data,
						}
					}
				}),

			setError: (error: string | null) =>
				set((state) => {
					state.error = error
				}),

			saveEnpsConfig: () => {
				const state = get()
				if (!state.enpsConfig) throw new Error('No NPS config data to save')
				return {
					name: state.enpsConfig.name,
					description: state.enpsConfig.description,
					cadence: state.enpsConfig.cadence,
					populationId: state.enpsConfig.populationId,
					emailTemplateId: state.enpsConfig.emailTemplateId,
					questionText: state.enpsConfig.questionText,
					followUpQuestionText: state.enpsConfig.followUpQuestionText,
					textFeedbackEnabled: state.enpsConfig.textFeedbackEnabled,
					allowExternalParticipants: state.enpsConfig.allowExternalParticipants,
					nextScheduledDate: state.enpsConfig.nextScheduledDate,
				}
			},

			useCreateEnpsConfig: () => {
				const queryClient = useQueryClient()
				const toast = useToast()
				const navigate = useNavigate()
				const enpsConfig = get().enpsConfig

				const createMutation = useMutation({
					...trpc.admin.enps.create.mutationOptions(),
					onSuccess: (data) => {
						toast.showToast('NPS configuration created successfully')
						if (enpsConfig?.organizationId) {
							navigate(`/organizations/${enpsConfig.organizationId}/enps/${data.id}/general`)
						}
					},
					onError: () => {
						toast.showToast('Error creating NPS configuration', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({ queryKey: [['enps']] })
					},
				})

				return {
					createEnpsConfig: async (data: EnpsFormData) => {
						if (!enpsConfig?.organizationId) return
						await createMutation.mutateAsync({
							organizationId: enpsConfig.organizationId,
							name: data.name,
							description: data.description ?? undefined,
							cadence: data.cadence,
							populationId: data.populationId ?? undefined,
							emailTemplateId: data.emailTemplateId ?? undefined,
							questionText: data.questionText,
							followUpQuestionText: data.followUpQuestionText,
							textFeedbackEnabled: data.textFeedbackEnabled,
							allowExternalParticipants: data.allowExternalParticipants,
						})
					},
					isLoading: createMutation.isPending,
				}
			},

			useEditEnpsConfig: (enpsId?: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()
				const enpsConfig = get().enpsConfig

				const editMutation = useMutation({
					...trpc.admin.enps.update.mutationOptions(),
					onSuccess: () => {
						toast.showToast('NPS configuration updated successfully')
					},
					onError: () => {
						toast.showToast('Error updating NPS configuration', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.search.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.getById.queryKey({ id: enpsId }),
							exact: false,
							refetchType: 'all',
						})
					},
				})

				return {
					editEnpsConfig: async (data: Partial<EnpsFormData>) => {
						if (!enpsConfig?.id || !enpsConfig?.organizationId) return
						await editMutation.mutateAsync({
							id: enpsConfig.id,
							organizationId: enpsConfig.organizationId,
							name: data.name,
							description: data.description ?? undefined,
							cadence: data.cadence,
							populationId: data.populationId,
							emailTemplateId: data.emailTemplateId,
							questionText: data.questionText,
							followUpQuestionText: data.followUpQuestionText,
							textFeedbackEnabled: data.textFeedbackEnabled,
							allowExternalParticipants: data.allowExternalParticipants,
							nextScheduledDate: data.nextScheduledDate,
						})
					},
					isLoading: editMutation.isPending,
				}
			},

			useActivateEnpsConfig: (enpsId: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()
				const enpsConfig = get().enpsConfig

				const activateMutation = useMutation({
					...trpc.admin.enps.activate.mutationOptions(),
					onSuccess: () => {
						toast.showToast('NPS configuration activated')
					},
					onError: () => {
						toast.showToast('Error activating NPS configuration', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.search.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.getById.queryKey({ id: enpsId }),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.getConfigResults.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.getActiveExecution.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.searchViewers.queryKey(),
							exact: false,
							refetchType: 'all',
						})
					},
				})

				return {
					activateEnpsConfig: async () => {
						if (!enpsConfig?.organizationId) return
						await activateMutation.mutateAsync({
							id: enpsId,
							organizationId: enpsConfig.organizationId,
						})
					},
					isLoading: activateMutation.isPending,
				}
			},

			usePauseEnpsConfig: (enpsId: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()
				const enpsConfig = get().enpsConfig

				const pauseMutation = useMutation({
					...trpc.admin.enps.pause.mutationOptions(),
					onSuccess: () => {
						toast.showToast('NPS configuration paused')
					},
					onError: () => {
						toast.showToast('Error pausing NPS configuration', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.search.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.getById.queryKey({ id: enpsId }),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.getConfigResults.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.getActiveExecution.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.searchViewers.queryKey(),
							exact: false,
							refetchType: 'all',
						})
					},
				})

				return {
					pauseEnpsConfig: async () => {
						if (!enpsConfig?.organizationId) return
						await pauseMutation.mutateAsync({
							id: enpsId,
							organizationId: enpsConfig.organizationId,
						})
					},
					isLoading: pauseMutation.isPending,
				}
			},

			useArchiveEnpsConfig: (enpsId: string) => {
				const queryClient = useQueryClient()
				const toast = useToast()
				const enpsConfig = get().enpsConfig

				const archiveMutation = useMutation({
					...trpc.admin.enps.archive.mutationOptions(),
					onSuccess: () => {
						toast.showToast('NPS configuration archived')
					},
					onError: () => {
						toast.showToast('Error archiving NPS configuration', 'error')
					},
					onSettled: () => {
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.search.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.getById.queryKey({ id: enpsId }),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.getConfigResults.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.getActiveExecution.queryKey(),
							exact: false,
							refetchType: 'all',
						})
						queryClient.invalidateQueries({
							queryKey: trpc.admin.enps.searchViewers.queryKey(),
							exact: false,
							refetchType: 'all',
						})
					},
				})

				return {
					archiveEnpsConfig: async () => {
						if (!enpsConfig?.organizationId) return
						await archiveMutation.mutateAsync({
							id: enpsId,
							organizationId: enpsConfig.organizationId,
						})
					},
					isLoading: archiveMutation.isPending,
				}
			},
		})),
		{ name: 'enps-store' },
	),
)
